import React, { useState } from 'react';
import { X, Target, BarChart3 } from 'lucide-react';
import { apiService } from '../../services/api';
import { CreateMetricRequest } from '../../types';

interface CreateMetricModalProps {
  goalId: string;
  goalName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateMetricModal: React.FC<CreateMetricModalProps> = ({
  goalId,
  goalName,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    name: '',
    unit: '',
    metricType: 'absolute' as 'absolute' | 'rate' | 'binary',
    targetValue: '',
    targetDate: '',
    isPrimary: false,
    weight: '1.0'
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Metric name is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const requestData: CreateMetricRequest = {
        goalId,
        name: formData.name.trim(),
        unit: formData.unit.trim() || undefined,
        metricType: formData.metricType,
        targetValue: formData.targetValue ? parseFloat(formData.targetValue) : undefined,
        targetDate: formData.targetDate || undefined,
        isPrimary: formData.isPrimary,
        weight: parseFloat(formData.weight)
      };

      await apiService.createMetric(requestData);
      onSuccess();
    } catch (err) {
      console.error('Metric creation failed:', err);
      setError('Failed to create metric');
    } finally {
      setLoading(false);
    }
  };

  const metricTypes = [
    { 
      value: 'absolute', 
      label: 'Absolute', 
      description: 'Fixed number (e.g., 100 sales, 50 users)' 
    },
    { 
      value: 'rate', 
      label: 'Rate', 
      description: 'Per time unit (e.g., 10 sales/month, 5 users/day)' 
    },
    { 
      value: 'binary', 
      label: 'Binary', 
      description: 'Done/Not Done (e.g., Feature launched, Milestone reached)' 
    }
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-6 h-6 text-blue-600" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Add Metric
                </h2>
                <p className="text-sm text-gray-500">
                  For: {goalName}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Metric Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-field"
                  placeholder="e.g., User Signups, Revenue, Features Completed..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Metric Type *
                </label>
                <div className="space-y-3">
                  {metricTypes.map((type) => (
                    <label key={type.value} className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="metricType"
                        value={type.value}
                        checked={formData.metricType === type.value}
                        onChange={(e) => setFormData({ ...formData, metricType: e.target.value as 'absolute' | 'rate' | 'binary' })}
                        className="mt-1 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{type.label}</div>
                        <div className="text-sm text-gray-500">{type.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unit
                  </label>
                  <input
                    type="text"
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    className="input-field"
                    placeholder="e.g., users, €, %"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Weight (0.1 - 5.0)
                  </label>
                  <input
                    type="number"
                    min="0.1"
                    max="5.0"
                    step="0.1"
                    value={formData.weight}
                    onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                    className="input-field"
                    placeholder="1.0"
                  />
                </div>
              </div>

              {formData.metricType !== 'binary' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Value
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={formData.targetValue}
                    onChange={(e) => setFormData({ ...formData, targetValue: e.target.value })}
                    className="input-field"
                    placeholder="Goal value to reach"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Date
                </label>
                <input
                  type="date"
                  value={formData.targetDate}
                  onChange={(e) => setFormData({ ...formData, targetDate: e.target.value })}
                  className="input-field"
                />
              </div>

              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="isPrimary"
                    type="checkbox"
                    checked={formData.isPrimary}
                    onChange={(e) => setFormData({ ...formData, isPrimary: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="isPrimary" className="font-medium text-gray-700">
                    Primary Metric
                  </label>
                  <p className="text-gray-500">
                    This is the most important metric for this goal
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex items-center gap-2"
              >
                <Target className="w-4 h-4" />
                {loading ? 'Creating...' : 'Create Metric'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};